import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  applyActionCode,
  confirmPasswordReset,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  verifyPasswordResetCode,
} from "firebase/auth";

import { useFirebase } from "./firebaseProvider";

import { useNavigate } from "react-router-dom";

import {
  getUserInfo,
  signup,
  updateUserInfo,
  getLegalStatus,
} from "services/userService";
import { checkIn as streakCheckIn } from "services/streakService";
import {
  KEY_TOKEN,
  KEY_USER,
  KEY_LOGOUT_REASON,
  LOGOUT_REASON_ACCOUNT_DISABLED,
  ACCOUNT_DISABLED_MESSAGE,
} from "utils/constants";

import { identificarUsuario } from "analytics/posthogClient";

import {
  trackLoginSuccess,
  trackLoginFailed,
  trackLogout,
  trackTokenRefreshFailed,
} from "analytics/events";

import logger from "utils/logger";
import { describeSignupError } from "utils/signupErrors";
import { signInAcceptingLegacyEmail } from "utils/legacyEmail";

export const AuthContext = createContext({});

/**
 * Marca que a sessão caiu porque a conta foi desabilitada (banimento), para a
 * tela de login explicar o que houve. O Firebase derruba a sessão sozinho ao
 * falhar a renovação do token; sem esta marca o usuário só via o app voltar
 * para a tela de login, sem nenhuma mensagem.
 */
const markAccountDisabled = (error) => {
  if (error?.code !== "auth/user-disabled") return;
  try {
    sessionStorage.setItem(KEY_LOGOUT_REASON, LOGOUT_REASON_ACCOUNT_DISABLED);
  } catch (_) {
    // navegador sem sessionStorage (modo restrito): segue sem o aviso
  }
};

export const AuthProvider = ({ children }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showGenderModal, setShowGenderModal] = useState(false);
  // Status de verificação do e-mail no Firebase. Começa como `true` para não
  // exibir o gate antes do Firebase resolver o estado de autenticação.
  const [emailVerified, setEmailVerified] = useState(true);
  // Documentos legais cuja versão vigente o usuário ainda não aceitou. Começa
  // vazio pelo mesmo motivo: o gate só aparece depois que a API responde.
  const [pendingLegalDocuments, setPendingLegalDocuments] = useState([]);
  const { myAuth } = useFirebase();

  // Contas criadas antes de out/2026 existem no Auth com o e-mail reescrito
  // pela API ("joao.silva@gmail.com" virou "joaosilva@gmail.com").
  const signInAcceptingLegacy = (email, password) =>
    signInAcceptingLegacyEmail(
      (e, p) => signInWithEmailAndPassword(myAuth, e, p),
      email,
      password,
    );

  // Verificar se há usuário salvo no localStorage na inicialização
  useEffect(() => {
    const storedUser = localStorage.getItem(KEY_USER);
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
      } catch (error) {
        logger.error("Erro ao parsear usuário do localStorage:", error);
        localStorage.removeItem(KEY_USER);
      }
    }
  }, []);

  const performLogout = async () => {
    trackLogout(userRef.current?.uid);
    try {
      await signOut(myAuth);
      setUser(null);
      localStorage.removeItem(KEY_USER);
      localStorage.removeItem(KEY_TOKEN);
      navigate("/", { replace: true });
    } catch (error) {
      logger.error("Erro ao fazer logout:", error);
      setUser(null);
      localStorage.clear();
      navigate("/", { replace: true });
    }
  };

  // Mantém o valor mais recente de `user` acessível dentro do handler estável,
  // sem precisar recriá-lo (e re-registrar o listener) a cada mudança.
  const userRef = useRef(user);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // Handler estável: registrado uma única vez. Eventos `storage` só disparam em
  // OUTRAS abas, então não há risco de reagir ao próprio setItem — por isso o
  // antigo dance de remove/add em login/logout era inócuo e foi removido.
  const handleStorageChange = useCallback((event) => {
    if (event.key === "logout-event" && userRef.current) {
      performLogout();
    } else if (event.key === KEY_USER && !userRef.current) {
      try {
        const storedUser = JSON.parse(localStorage.getItem(KEY_USER));
        if (storedUser) {
          setUser(storedUser);
          // Cross-tab login: leva ao destino correto conforme o papel.
          navigate(
            storedUser.role === "ADMIN"
              ? "/admin/dashboard"
              : "/dash/user-dashboard",
            { replace: true },
          );
        }
      } catch (error) {
        logger.error("Erro ao ler usuário do localStorage (storage event):", error);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const logout = () => {
    localStorage.setItem("logout-event", Date.now());
    performLogout();
  };

  useEffect(() => {
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, [handleStorageChange]);

  const login = (email, password, callbackSuccess, callbackError) => {
    // Se não houver callbacks, retornar Promise (para admin login)
    if (!callbackSuccess && !callbackError) {
      const loginAsync = async () => {
        const userCredential = await signInAcceptingLegacy(email, password);

        // Usa getIdToken() (sempre um token fresco) em vez de
        // stsTokenManager.accessToken, para não persistir um token já expirado.
        const idToken = await userCredential.user.getIdToken();
        localStorage.setItem(KEY_TOKEN, idToken);

        // Buscar informações completas do usuário (incluindo role)
        const userInfoResponse = await getUserInfo();
        const fullUser = userInfoResponse.data;

        setUser(fullUser);
        localStorage.setItem(KEY_USER, JSON.stringify(fullUser));
        trackLoginSuccess(userCredential.user.uid);

        return { user: fullUser };
      };
      return loginAsync().catch((error) => {
        logger.error("Erro ao realizar login:", error);
        trackLoginFailed(error?.code || error?.message || "unknown");
        throw error;
      });
    }

    // Comportamento original com callbacks (para login normal)
    signInAcceptingLegacy(email, password)
      .then(async (userCredential) => {
        const firebaseUser = userCredential.user;

        // Registra o status de verificação. Se o e-mail não estiver
        // verificado, o login prossegue, mas o gate de verificação será
        // exibido por cima da aplicação (ver EmailVerificationModal).
        setEmailVerified(firebaseUser.emailVerified);

        // Usa getIdToken() (sempre um token fresco) em vez de
        // stsTokenManager.accessToken, que pode já estar expirado.
        const idToken = await firebaseUser.getIdToken();
        localStorage.setItem(KEY_TOKEN, idToken);

        reloadUserInfo(
          (user) => {
            trackLoginSuccess(firebaseUser.uid);
            // Redirecionar baseado no role do usuário
            if (user.role && user.role === "ADMIN") {
              navigate("/admin/dashboard", { replace: true });
            } else {
              navigate("/dash/user-dashboard", { replace: true });
            }
            callbackSuccess(user);
          },
          (err) => {
            callbackError(err);
          },
        );
      })
      .catch((error) => {
        logger.error("Erro ao realizar login:", error);
        trackLoginFailed(error?.code || error?.message || "unknown");
        let msg = "E-mail ou senha incorretos.";
        switch (error.code) {
          case "auth/too-many-requests":
            msg = "Muitas tentativas de login. Tente novamente mais tarde.";
            break;
          case "auth/invalid-email":
            msg = "E-mail inválido";
            break;
          case "auth/user-disabled":
            // Conta banida. Dizer só "desativada" deixava a pessoa sem saber o
            // motivo, sem prazo e sem canal — e o app inteiro exige login, então
            // ela não tem como abrir um chamado por dentro dele.
            msg = ACCOUNT_DISABLED_MESSAGE;
            break;
          case "auth/network-request-failed":
            msg =
              "Sem conexão com o servidor. Verifique sua internet e tente novamente.";
            break;
          // Com a proteção contra enumeração de e-mail (padrão no SDK atual),
          // senha errada e conta inexistente chegam como invalid-credential.
          // Mensagem única para os três: dizer qual dos dois errou ajudaria
          // quem tenta descobrir se um e-mail tem conta.
          case "auth/invalid-credential":
          case "auth/invalid-login-credentials":
          case "auth/user-not-found":
          case "auth/wrong-password":
            msg = "E-mail ou senha incorretos.";
            break;
          default:
            // Nunca o error.message: é o texto cru do Firebase, em inglês.
            msg = "Não foi possível entrar agora. Tente novamente em instantes.";
        }
        callbackError(msg);
      });
  };

  const updateUser = (newUserInfo, callbackSuccess, callbackError) => {
    updateUserInfo(newUserInfo)
      .then((res) => {
        // Prefere o estado retornado pelo servidor (que pode normalizar/mesclar
        // campos) e cai para o payload otimista se a resposta vier vazia.
        const updated = res?.data ?? newUserInfo;
        setUser(updated);
        localStorage.setItem(KEY_USER, JSON.stringify(updated));
        callbackSuccess?.(updated);
      })
      .catch((error) => {
        logger.error("Erro ao atualizar informações do usuário:", error);
        callbackError?.(error);
      });
  };

  /**
   * Consulta quais documentos legais o usuário ainda não aceitou na versão
   * vigente.
   *
   * Falha de rede mantém o estado anterior em vez de assumir pendência: um
   * gate bloqueante disparado por instabilidade momentânea tranca o app inteiro
   * sem que nada tenha mudado nos documentos.
   */
  const refreshLegalStatus = async () => {
    try {
      const res = await getLegalStatus();
      const pending = res?.data?.data?.pending;
      setPendingLegalDocuments(Array.isArray(pending) ? pending : []);
      return pending;
    } catch (error) {
      logger.error("Erro ao verificar documentos legais:", error);
      return null;
    }
  };

  const reloadUserInfo = async (callbackSuccess, callbackError) => {
    getUserInfo()
      .then((res) => {
        setUser(res.data);
        localStorage.setItem(KEY_USER, JSON.stringify(res.data));
        // Verificar se o usuário precisa definir o gênero
        if (!res.data.gender || res.data.gender === "") {
          setShowGenderModal(true);
        }
        // Check-in diário de streak (fire-and-forget, não bloqueia login)
        streakCheckIn().catch(() => {});
        refreshLegalStatus();
        callbackSuccess?.(res.data);
      })
      .catch(() => {
        callbackError?.("Erro ao buscar informações do usuário");
      });
  };

  // Renovação ativa do token NÃO é feita aqui.
  //
  // O Firebase já renova o ID token sozinho (~5 min antes de expirar) e o
  // `onIdTokenChanged` abaixo persiste cada token novo. Um `useQuery` com
  // `getIdToken(true)` em cima disso criava um laço fechado de renovação:
  //
  //   getIdToken(true) → token muda → onIdTokenChanged dispara →
  //   invalidateQueries → getIdToken(true) → ...
  //
  // Em rede rápida o laço morria sozinho (dois refreshes no mesmo segundo
  // devolvem o MESMO JWT, então o token não "muda" e o listener não dispara).
  // Em latência de celular, não: o segundo virava antes do refresh terminar, o
  // JWT mudava a cada volta e o laço não parava — dezenas de chamadas por
  // minuto a `securetoken.googleapis.com` por aba logada. É o que estourava a
  // cota do projeto (auth/quota-exceeded) com pouquíssimos usuários reais.
  //
  // O refresh no retorno de foco (quando o navegador pode ter feito throttling
  // da aba em segundo plano e deixado o token vencer) é preservado abaixo, mas
  // SEM reinjetar nada no fluxo do listener.
  useEffect(() => {
    const renovarNoFoco = async () => {
      const currentUser = myAuth.currentUser;
      if (!currentUser) return;
      try {
        await currentUser.getIdToken(true);
        // Não gravamos o token aqui: `onIdTokenChanged` recebe o token novo e
        // persiste. Uma escrita a mais aqui só reabriria caminho para corrida.
      } catch (error) {
        logger.error("Erro ao renovar token no foco:", error);
        trackTokenRefreshFailed(error, "window_focus");
        markAccountDisabled(error);
      }
    };
    window.addEventListener("focus", renovarNoFoco);
    return () => window.removeEventListener("focus", renovarNoFoco);
  }, [myAuth]);

  useEffect(() => {
    const unsubscribeAuth = myAuth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        // Correlaciona os eventos (PostHog) a este usuário — só de fato
        // identifica se houver consentimento analítico.
        identificarUsuario({
          uid: firebaseUser.uid,
          email: firebaseUser.email,
        });
        // Mantém o gate de verificação sincronizado com o status real do
        // Firebase (cobre login, recarga de página e sessões persistidas).
        setEmailVerified(firebaseUser.emailVerified);
        // Usa getIdToken() (que renova quando necessário) em vez de
        // firebaseUser.accessToken, que pode estar expirado ao recarregar a
        // página e causaria 401 na primeira requisição.
        try {
          const idToken = await firebaseUser.getIdToken();
          localStorage.setItem(KEY_TOKEN, idToken);
        } catch (tokenError) {
          logger.error("Erro ao obter ID token no boot:", tokenError);
          trackTokenRefreshFailed(tokenError, "boot");
          markAccountDisabled(tokenError);
        }
        reloadUserInfo(
          () => setLoading(false), // Callback success
          () => setLoading(false), // Callback error
        );
      } else {
        identificarUsuario(null);
        setEmailVerified(true);
        setUser(null);
        localStorage.removeItem(KEY_USER);
        localStorage.removeItem(KEY_TOKEN);
        setLoading(false);
      }
    });

    const unsubscribeToken = myAuth.onIdTokenChanged(async (firebaseUser) => {
      // Mantém o token persistido sincronizado com o renovado pelo Firebase.
      if (firebaseUser) {
        try {
          const idToken = await firebaseUser.getIdToken();
          localStorage.setItem(KEY_TOKEN, idToken);
        } catch (tokenError) {
          logger.error("Erro ao atualizar ID token:", tokenError);
          trackTokenRefreshFailed(tokenError, "onIdTokenChanged");
          markAccountDisabled(tokenError);
        }
      }
    });

    return () => {
      unsubscribeAuth();
      unsubscribeToken();
    };
  }, [myAuth]);

  const signUp = async (userForm, callbackSuccess, callbackError) => {
    try {
      await signup(userForm);
      const userCredential = await signInWithEmailAndPassword(
        myAuth,
        userForm.email,
        userForm.password,
      );
      const idToken = await userCredential.user.getIdToken();

      localStorage.setItem(KEY_TOKEN, idToken);

      // Envia o e-mail de verificação logo após criar a conta.
      try {
        await sendEmailVerification(userCredential.user, {
          url: `${window.location.origin}/user-login`,
        });
      } catch (verificationError) {
        logger.error(
          "Erro ao enviar e-mail de verificação:",
          verificationError,
        );
      }

      callbackSuccess(userCredential.user.uid);
    } catch (error) {
      // Sem isto o cadastro falhava em silêncio: nada no console, e o usuário
      // via só a mensagem genérica.
      logger.error("Erro no cadastro:", error, {
        status: error?.response?.status,
        data: error?.response?.data,
      });
      callbackError(describeSignupError(error));
    }
  };

  const sendEmailResetPassword = (email) => {
    // continueUrl: após o reset pela página padrão do Firebase, redireciona ao login
    const continueUrl = `${window.location.origin}/user-login`;
    return sendPasswordResetEmail(myAuth, email, { url: continueUrl });
  };
  const checkResetPasswordCode = (oobCode) => {
    // verifyPasswordResetCode é específico para reset de senha e retorna o e-mail
    // associado ao código, lançando erro para códigos inválidos/expirados.
    return verifyPasswordResetCode(myAuth, oobCode);
  };

  const applyActionCodeVerification = (oobCode) => {
    return applyActionCode(myAuth, oobCode);
  };

  const confirmNewPassword = (code, newPassword) => {
    return confirmPasswordReset(myAuth, code, newPassword);
  };

  // Chamado pelo GenderRequiredModal após salvar o gênero com sucesso
  const setGenderDefined = (gender) => {
    setShowGenderModal(false);
    setUser((prev) => {
      if (!prev) return prev;
      const updated = { ...prev, gender };
      localStorage.setItem(KEY_USER, JSON.stringify(updated));
      return updated;
    });
  };

  const resendEmailVerification = async () => {
    if (myAuth.currentUser) {
      await sendEmailVerification(myAuth.currentUser, {
        url: `${window.location.origin}/user-login`,
      });
    }
  };

  // Recarrega o usuário do Firebase para checar se o e-mail já foi verificado.
  // Atualiza o estado do gate e, quando confirmado, sincroniza token e dados.
  const refreshEmailVerified = async () => {
    const current = myAuth.currentUser;
    if (!current) return false;
    await current.reload();
    const verified = myAuth.currentUser?.emailVerified ?? false;
    setEmailVerified(verified);
    if (verified) {
      try {
        const token = await myAuth.currentUser.getIdToken(true);
        localStorage.setItem(KEY_TOKEN, token);
      } catch (error) {
        logger.error("Erro ao atualizar token após verificação:", error);
      }
      reloadUserInfo();
    }
    return verified;
  };

  // Gate de verificação: usuário logado, e-mail não verificado.
  // Administradores são isentos para não bloquear o acesso ao painel.
  const showEmailVerificationModal =
    !!user && emailVerified === false && user?.role !== "ADMIN";

  // Gate de re-aceite: usuário logado com documento pendente. Admins também
  // aceitam — a atualização vale para todo mundo, e isentá-los abriria o
  // precedente de conta ativa sem aceite registrado.
  const showLegalReacceptanceModal =
    !!user && pendingLegalDocuments.length > 0;

  const theValues = useMemo(
    () => ({
      user,
      loading,
      login,
      logout,
      signUp,
      checkResetPasswordCode,
      resendEmailVerification,
      refreshEmailVerified,
      emailVerified,
      showEmailVerificationModal,
      confirmNewPassword,
      sendEmailResetPassword,
      applyActionCodeVerification,
      reloadUserInfo,
      updateUser,
      showGenderModal,
      setGenderDefined,
      pendingLegalDocuments,
      showLegalReacceptanceModal,
      refreshLegalStatus,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [user, loading, showGenderModal, emailVerified, pendingLegalDocuments],
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50 dark:bg-gray-900">
        <svg
          className="animate-spin h-12 w-12 text-pink-500"
          viewBox="0 0 24 24"
        >
          <circle
            className="opacity-25"
            cx="12"
            cy="12"
            r="10"
            stroke="currentColor"
            strokeWidth="4"
            fill="none"
          />
          <path
            className="opacity-75"
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={theValues}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
